import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import ReactHighlighter from 'react-highlight-words';
import Select, { components, createFilter } from 'react-select';
import useOnClickOutside from 'ui_system/utilities/useOnClickOutside';
import { ArrowDropDown } from 'views/ui/icons';
import SearchThin from 'views/ui/icons/search-thin-icon';
import Checkmark from 'views/ui/icons/checkmark-icon';
import defaultImage from 'views/anyguide/tours/item/images/default_image.png';
import ClearIcon from 'views/ui/icons/cancel-msg-icon';
import { colors, fontSizes, shadows, shape, spacing } from '../utils';

const MultiSearchSelectContainer = styled.div`
  width: max-content;
`;

// Custom Input Component & Styles
const InputContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  left: 24px;
  position: absolute;
  width: 302px;
`;

const Input = (props) => {
  // eslint-disable-next-line prettier/prettier
  const {
    selectProps: { inputValue, onClearSearch },
  } = props;

  return (
    <InputContainer>
      <components.Input {...props} />
      {inputValue && (
        <ClearIcon
          fill={colors.text.icon}
          height="10px"
          onClick={onClearSearch}
          width="10px"
        />
      )}
    </InputContainer>
  );
};

// Custom Menu Component & Styles
const ApplyButtonContainer = styled.div`
  border-top: 1px solid ${colors.border};
  display: flex;
  justify-content: center;
  padding: ${spacing.medium} 0;
  position: relative;
`;

const ApplyButton = styled.button`
  background: ${colors.secondary};
  border: none;
  border-radius: ${shape.borderRadius};
  color: ${colors.white};
  cursor: pointer;
  font-size: ${fontSizes.medium};
  height: 38px;
  width: 130px;
`;

const Menu = (props) => {
  const {
    children,
    selectProps: { name, onChange },
  } = props;

  const onApply = (e) => {
    e.stopPropagation();
    const actionMeta = {
      action: 'apply-options',
      name,
      srcEvent: e,
    };
    onChange([], actionMeta);
  };

  return (
    <components.Menu {...props}>
      {children}
      <ApplyButtonContainer>
        <ApplyButton onClick={onApply}>Apply</ApplyButton>
      </ApplyButtonContainer>
    </components.Menu>
  );
};

// Custom MenuList Component & Styles
const SelectActions = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${spacing.xsmall} ${spacing.small};
`;

const buttonLink = (props) => css`
  border: none;
  background: transparent;
  color: ${colors.text.link};
  cursor: ${props.disabled ? 'default' : 'pointer'};
  font-size: 14px;
  opacity: ${props.disabled ? '0.5' : '1'};
  padding: ${spacing.xxsmall};
  visibility: ${props.hide ? 'hidden' : 'visible'};
`;

const SelectAll = styled.button`
  ${buttonLink};
`;

const ShowSelected = styled.button`
  ${buttonLink};
`;

const MenuList = (props) => {
  const {
    children,
    selectProps: {
      allSelected,
      onChange,
      inputValue,
      name,
      searchResults,
      showSelected,
      value,
    },
  } = props;
  const resultsLength = children.length;
  const results = Object.values(searchResults);
  const selectedLength = (value && value.length) || 0;
  const selectString =
    resultsLength && inputValue
      ? `Select all ${resultsLength} results`
      : 'Select all';
  const showString = showSelected
    ? 'Show all'
    : `Show selected (${selectedLength})`;

  const onSelectAll = () => {
    const actionMeta = {
      action: inputValue ? 'select-results' : 'select-all',
      name,
      option: results,
    };
    onChange(results, actionMeta);
  };

  const onShowSelected = () => {
    const actionMeta = {
      action: showSelected ? 'show-all' : 'show-selected',
      name,
    };
    onChange([], actionMeta);
  };

  return (
    <components.MenuList {...props}>
      <SelectActions>
        <SelectAll
          disabled={allSelected || showSelected}
          hide={showSelected}
          onClick={onSelectAll}
        >
          {selectString}
        </SelectAll>
        <ShowSelected
          disabled={!selectedLength}
          hide={allSelected}
          onClick={onShowSelected}
        >
          {showString}
        </ShowSelected>
      </SelectActions>
      {children}
    </components.MenuList>
  );
};

// Custom Option Component & Styles
const CheckboxContainer = styled.div`
  box-sizing: border-box;
  display: grid;
  grid-template-areas: 'checkbox';
  margin-right: ${spacing.medium};
`;

const CheckmarkIcon = styled(Checkmark)`
  fill: ${colors.white};
  height: 6px;
  visibility: ${(props) =>
    props.isselected === 'true' ? 'visible' : 'hidden'};
`;

const HiddenCheckbox = styled.input`
  box-sizing: border-box;
  grid-area: checkbox;
  height: 16px;
  margin: 0;
  opacity: 0;
  width: 16px;
`;

const StyledCheckbox = styled.div`
  align-items: center;
  background: ${(props) =>
    props.isSelected ? colors.secondary : colors.white};
  border: 1px solid
    ${(props) => (props.isSelected ? colors.secondary : colors.border)};
  border-radius: 0;
  box-sizing: border-box;
  display: flex;
  grid-area: checkbox;
  justify-content: center;
  height: 16px;
  width: 16px;
`;

const Checkbox = (props) => {
  const { isSelected } = props;

  return (
    <CheckboxContainer>
      <HiddenCheckbox checked={isSelected} readOnly type="checkbox" />
      <StyledCheckbox {...props} isSelected={isSelected}>
        <CheckmarkIcon isselected={isSelected.toString()} />
      </StyledCheckbox>
    </CheckboxContainer>
  );
};

const ThumbnailWrapper = styled.object`
  height: 30px;
  margin-right: ${spacing.small};
  width: 30px;
`;

const Thumbnail = styled.img`
  height: 30px;
  width: 30px;
`;

const Highlighter = styled(ReactHighlighter)`
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const Highlight = styled.span`
  font-weight: 700;
`;

const OnlyButton = styled.button`
  ${buttonLink};
  margin-left: ${spacing.xsmall};
  padding: ${spacing.xxsmall};
  &:hover {
    text-decoration: underline;
  }
`;

const defaultImageSrc =
  'https://www.showplacerents.com/img/default-placeholder.png';

const Option = (props) => {
  const {
    children,
    data,
    innerProps,
    isFocused,
    selectProps: { inputValue, isOptionSelected, name, onChange, showSelected },
  } = props;
  const { image = defaultImageSrc } = data;
  const searchWords = inputValue.split(' ');
  const [isHovered, setIsHovered] = useState(false);
  const isSelected = isOptionSelected(data);

  if (showSelected && !isSelected) {
    return null;
  }

  const onOnlyClick = (e) => {
    e.stopPropagation();
    const actionMeta = {
      action: 'select-only-option',
      name,
      option: data,
    };
    onChange(data, actionMeta);
  };
  const onMouseEnter = () => {
    setIsHovered(true);
  };
  const onMouseLeave = () => {
    setIsHovered(false);
  };
  const originalOnClick = innerProps.onClick;
  const onClick = (e) => {
    // Prevent a false trigger of onClickOutside
    e.stopPropagation();
    originalOnClick(e);
  };
  const newInnerProps = { ...innerProps, onClick, onMouseEnter, onMouseLeave };

  return (
    <components.Option {...props} innerProps={newInnerProps}>
      <Checkbox {...props} innerProps={newInnerProps} isSelected={isSelected} />
      <ThumbnailWrapper data={image} type="image/png">
        <Thumbnail src={defaultImageSrc} alt="" />
      </ThumbnailWrapper>
      <Highlighter
        activeIndex={-1}
        highlightTag={Highlight}
        searchWords={searchWords}
        textToHighlight={children}
      />
      {isFocused && isHovered && (
        <OnlyButton onClick={onOnlyClick}>only</OnlyButton>
      )}
    </components.Option>
  );
};

// Custom ValueContainer Component & Styles
const SearchIcon = styled(SearchThin)`
  fill: ${colors.text.icon};
  height: 16px;
  margin-right: ${spacing.xsmall};
  width: 16px;
`;

const ValueContainer = (props) => {
  const { children } = props;

  return (
    <components.ValueContainer {...props}>
      <SearchIcon />
      {children}
    </components.ValueContainer>
  );
};

// Custom Control Component
const Control = (props) => {
  const { children } = props;

  return <components.Control {...props}>{children}</components.Control>;
};

// Dropdown Component & Styles -- exists outside react-select
const DropdownWrapper = styled.div`
  align-items: center;
  background: ${colors.white};
  border: 1px solid ${colors.borderAction};
  box-sizing: border-box;
  color: ${colors.text.primary};
  display: flex;
  flex-wrap: nowrap;
  font-size: ${fontSizes.medium};
  height: 36px;
  justify-content: space-between;
  padding: ${spacing.none} ${spacing.medium};
  width: 360px;
`;

const DropdownLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownTooltip = styled.div`
  background: ${colors.background.light};
  box-shadow: ${shadows.primary};
  box-sizing: border-box;
  font-size: ${fontSizes.small};
  padding: ${spacing.xxsmall} ${spacing.small};
  position: absolute;
  width: 360px;
  z-index: 2;
`;

const ArrowDropDownIcon = styled(ArrowDropDown)`
  flex-shrink: 0;
  margin-left: ${spacing.medium};
`;

const Dropdown = (props) => {
  const {
    appliedAllSelected,
    appliedValue,
    open,
    options,
    selectAllLabel,
    setShowDropdownTooltip,
    toggleOpen,
  } = props;
  let tooltipTimer;
  const onMouseEnter = () => {
    if (!open && options.length) {
      tooltipTimer = setTimeout(() => setShowDropdownTooltip(true), 750);
    }
  };
  const onMouseOver = () => {
    if (!open && !tooltipTimer && options.length) {
      tooltipTimer = setTimeout(() => setShowDropdownTooltip(true), 750);
    }
  };
  const onMouseLeave = () => {
    clearTimeout(tooltipTimer);
    setShowDropdownTooltip(false);
  };
  const onClick = () => {
    toggleOpen();
    if (open) {
      setShowDropdownTooltip(false);
      clearTimeout(tooltipTimer);
    }
  };

  let experienceCount;
  let selectedString = appliedValue.map((option) => option.label).join(', ');
  if (appliedValue.length > 1) {
    experienceCount = (
      <Highlight>{appliedValue.length} Experiences: </Highlight>
    );
  }
  if (appliedAllSelected) {
    selectedString = selectAllLabel;
  }

  return (
    <DropdownWrapper
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
    >
      <DropdownLabel>
        {experienceCount}
        {selectedString}
      </DropdownLabel>
      <ArrowDropDownIcon />
    </DropdownWrapper>
  );
};

// Custom styles for built-in react-select components
const styles = {
  container: (base) => ({
    ...base,
    fontFamily: 'Soleil, proxima-nova, Sans-serif',
    fontSize: fontSizes.medium,
    position: 'absolute',
    width: '360px',
    zIndex: 2,
  }),
  control: (base) => ({
    ...base,
    border: `1px solid ${colors.borderAction}`,
    borderBottom: 'none',
    borderRadius: 'none',
    borderTop: 'none',
    boxShadow: 'none',
    boxSizing: 'border-box',
    padding: spacing.medium,
    paddingBottom: 0,
    paddingTop: spacing.small,
    '&:hover': {
      border: `1px solid ${colors.borderAction}`,
      borderBottom: 'none',
      borderTop: 'none',
    },
    '&::after': {
      boxShadow: `0px 0px 10px 0px ${colors.borderAction}`,
      clipPath: 'inset(-100% 0px 0px)',
    },
  }),
  indicatorsContainer: () => ({ display: 'none' }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  menu: (base) => ({
    ...base,
    background: colors.white,
    border: `1px solid ${colors.borderAction}`,
    borderRadius: 'none',
    borderTop: 'none',
    boxShadow: 'none',
    boxSizing: 'border-box',
    margin: 0,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: '367px',
    overflowY: 'auto',
    scrollbarColor: `${colors.border} ${colors.white}`,
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      WebkitAppearance: 'none',
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: colors.border,
      borderRadius: shape.borderRadius,
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    padding: `${spacing.xsmall} ${spacing.medium}`,
  }),
  option: (base, { isDisabled }) => ({
    ...base,
    background: colors.white,
    borderLeft: `${spacing.medium} solid ${colors.white}`,
    borderRight: `${spacing.small} solid ${colors.white}`,
    boxShadow: `inset 0 1px 0 ${colors.background.light}`,
    boxSizing: 'border-box',
    color: isDisabled ? colors.text.icon : colors.text.primary,
    cursor: 'pointer',
    fontSize: fontSizes.medium,
    fontWeight: '400',
    padding: spacing.none,

    // NEW
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    height: '44px',
    '&:active': {
      background: colors.background.light,
      borderColor: 'transparent',
      boxShadow: 'none',
    },
    '&:hover': {
      background: colors.background.light,
      borderColor: 'transparent',
      boxShadow: 'none',
    },
    '&:hover + div': {
      borderColor: 'transparent',
      boxShadow: 'none',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: colors.text.icon,
    margin: 0,
    position: 'static',
    top: 'auto',
    transform: 'none',
  }),
  singleValue: (base) => ({
    ...base,
    color: colors.text.primary,
    left: '24px',
    top: '10px',
  }),
  valueContainer: (base) => ({
    ...base,
    borderBottom: `1px solid ${colors.border}`,
    padding: 0,
    paddingBottom: spacing.xsmall,
  }),
};

// Custom getOptionValue function for react-select
const getOptionValue = (option = {}) => {
  if (typeof option.value === 'object') {
    return option.value.value;
  }
  return option.value;
};

const defaultSelectAllOption = {
  label: 'All experiences',
  value: 'SELECT_ALL',
};

export const MultiSearchSelect = ({
  appliedValue,
  defaultValue = defaultSelectAllOption,
  inputValue = '',
  name = 'MultiSearchSelect',
  onApply,
  onClose,
  onDeselect,
  onSelect,
  onSelectAll,
  onSelectOnly,
  options = [],
  searchPlaceholder = 'Search experiences',
  selectAllLabel = 'All experiences',
  selectAllOption = defaultSelectAllOption,
  value,
}) => {
  const ref = useRef(null);
  const [showSelected, setShowSelected] = useState(false);
  const [open, setOpen] = useState(false);
  const [inputVal, setInputValue] = useState(inputValue);
  const [searchResults, setSearchResults] = useState({});
  const [showDropdownTooltip, setShowDropdownTooltip] = useState(false);

  const onClearSearch = (e) => {
    // prevent false trigger of onClickOutside
    e.stopPropagation();
    setInputValue('');
    setSearchResults({});
  };

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
      setShowSelected(false);
      setInputValue('');
      setSearchResults({});
      onClose();
    } else {
      setOpen(true);
    }
  };

  useOnClickOutside(ref, (e) => {
    if (open) {
      setOpen(false);
      setShowSelected(false);
      onClearSearch(e);
      onClose();
    }
  });

  const filterOption = (option, currentInput) => {
    const { value: optionValue } = option;
    const filter = createFilter();
    const filtered = filter(option, currentInput);
    if (filtered && currentInput && !searchResults[optionValue]) {
      const newResults = { ...searchResults, [optionValue]: option };
      setSearchResults(newResults);
    }
    if (!filtered && searchResults[optionValue]) {
      delete searchResults[optionValue];
    }
    return filtered;
  };

  let showSelectedOptions;
  const valueSet = Object.values(value);
  const appliedValueSet = Object.values(appliedValue);

  const allSelected = !!value[selectAllOption.value];
  const appliedAllSelected = !!appliedValue[selectAllOption.value];
  let tooltipString = appliedValueSet.map((option) => option.label).join(', ');
  if (appliedAllSelected) {
    tooltipString = options.map((option) => option.label).join(', ');
  }

  const isOptionSelected = (option) => allSelected || !!value[option.value];

  const onInputChange = (newInput, { action }) => {
    if (action === 'input-change') {
      setInputValue(newInput);
    }
  };

  const onChange = (val, actionMeta) => {
    const { action, option, srcEvent } = actionMeta;
    if (action === 'select-option') {
      onSelect([option]);
    }
    if (action === 'deselect-option') {
      onDeselect(option);
    }
    if (action === 'select-only-option') {
      onSelectOnly(option);
    }
    if (action === 'select-all') {
      onSelectAll(selectAllOption);
    }
    if (action === 'show-selected') {
      setShowSelected(true);
    }
    if (action === 'show-all') {
      setShowSelected(false);
    }
    if (action === 'select-results') {
      onSelect(option);
    }
    if (action === 'apply-options') {
      onApply(value);
      setOpen(false);
      setShowSelected(false);
      onClearSearch(srcEvent);
    }
  };

  return (
    <MultiSearchSelectContainer ref={ref}>
      <Dropdown
        allSelected={allSelected}
        appliedAllSelected={appliedAllSelected}
        appliedValue={appliedValueSet}
        open={open}
        options={options}
        selectAllLabel={selectAllLabel}
        setShowDropdownTooltip={setShowDropdownTooltip}
        toggleOpen={toggleOpen}
        setOpen={setOpen}
        value={valueSet}
      />
      {showDropdownTooltip && !open && (
        <DropdownTooltip>{tooltipString}</DropdownTooltip>
      )}
      {open && (
        <Select
          backspaceRemovesValue={false}
          closeMenuOnSelect={false}
          components={{
            Control,
            DropdownIndicator: null,
            IndicatorSeparator: null,
            Input,
            Menu,
            MenuList,
            Option,
            ValueContainer,
          }}
          controlShouldRenderValue={false}
          defaultValue={defaultValue}
          filterOption={filterOption}
          getOptionValue={getOptionValue}
          hideSelectedOptions={false}
          inputValue={inputVal}
          isMulti
          isOptionSelected={isOptionSelected}
          maxMenuHeight={436}
          menuIsOpen
          name={name}
          onChange={onChange}
          onClearSearch={onClearSearch}
          onInputChange={onInputChange}
          options={showSelectedOptions || options}
          placeholder={searchPlaceholder}
          styles={styles}
          tabSelectsValue={false}
          value={valueSet}
          // custom props
          allSelected={allSelected}
          searchResults={searchResults}
          showSelected={showSelected}
        />
      )}
    </MultiSearchSelectContainer>
  );
};
